/** @file Compiles rig curve codes (0 linear, 1 stepped, [x1, cy1, x2, cy2] bezier) into a CurveTable. */
import type { CurveTable } from '../../skeletonData';
import { coreFail } from '../coreFail';

type Bezier = readonly [number, number, number, number];

/** A bezier row: exactly four finite numbers. */
function isBezier(code: unknown): code is Bezier {
  return Array.isArray(code) && code.length === 4 && (code as unknown[]).every((n) => typeof n === 'number' && Number.isFinite(n));
}

/**
 * One entry per segment (keyCount - 1); omitted = all linear; bezier rows copy x1, cy1, x2, cy2 into cp verbatim.
 * Each entry must be exactly `0` (linear), `1` (stepped) or a bezier of four finite numbers with x1 and x2 in
 * [0, 1] (the curve parameter must stay a function of time); anything else is VALIDATION at `<path>[segment]`.
 */
export function readCurves(c: readonly unknown[] | undefined, keyCount: number, path: string): CurveTable {
  const segments = Math.max(0, keyCount - 1);
  const kind = new Uint8Array(segments);
  const cp = new Float64Array(4 * segments);
  if (c === undefined) return { kind, cp };
  if (c.length !== segments) coreFail('VALIDATION', `${c.length} curves for ${segments} segments`, { path });
  c.forEach((code, s) => {
    if (code === 0) return;
    if (code === 1) {
      kind[s] = 1;
      return;
    }
    if (!isBezier(code)) {
      coreFail('VALIDATION', `curve ${JSON.stringify(code)} is not 0, 1 or a bezier [x1, cy1, x2, cy2] of four finite numbers`, { path: `${path}[${s}]` });
    } else if (code[0] < 0 || code[0] > 1 || code[2] < 0 || code[2] > 1) {
      coreFail('VALIDATION', `bezier x1 ${code[0]} and x2 ${code[2]} must lie in [0, 1]`, { path: `${path}[${s}]` });
    }
    kind[s] = 2;
    cp.set(code, 4 * s);
  });
  return { kind, cp };
}
